import styled from 'styled-components';
import { FaFacebook, FaLinkedinIn, FaReddit, FaCheck, FaShare } from 'react-icons/fa';
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import FacebookService from '../../services/FacebookService';
import LinkedInService from '../../services/LinkedInService';
import RedditService from '../../services/RedditService';
import SocialAuthService from '../../services/SocialAuthService';
import UserSessionManager from '../../utils/UserSessionManager';
import EventContext from "../../context/EventContext";
import { useEventStore } from "../../store/eventStore";


const platformColors = {
  facebook: "#1877f2",
  instagram: "#dc2743",
  linkedin: "#0a66c2",
  reddit: "#ff4500",
};

const AuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  margin: 0 auto;
`;

const AuthCard = styled.div`
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ platform }) => platformColors[platform] || "#667eea"};
    box-shadow: 0 4px 12px
      ${({ platform }) =>
    platformColors[platform]
      ? `${platformColors[platform]}40` // add opacity for shadow
      : "rgba(102, 126, 234, 0.15)"};
  }
`;

const AuthHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const PlatformIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 24px;
  color: white;
  background: ${({ platform }) => platformColors[platform] || "#667eea"};
`;

const PlatformInfo = styled.div`
  flex: 1;
`;

const PlatformName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
`;

const PlatformDescription = styled.p`
  color: #666;
  font-size: 14px;
`;

const AuthButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &.facebook {
    background: #1877f2;
    color: white;
    
    &:hover {
      background: #166fe5;
    }
  }
  
  &.instagram {
    background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
    color: white;
    
    &:hover {
      opacity: 0.9;
    }
  }

  &.linkedin {
    background: #0a66c2; // LinkedIn blue
    color: white;

    &:hover {
      background: #004182; // darker shade for hover
    }
  }

  &.reddit {
    background: #ff4500; // Reddit orange
    color: white;

    &:hover {
      background: #e03d00; // darker orange on hover
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FacebookPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 32px;
  color: #333;
`;

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 32px;
`;

const CheckPageIcon = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: ${props => props.selected ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const PageName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const PageCard = styled.div`
  border: 3px solid ${props => props.selected ? '#667eea' : '#e9ecef'};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? '#f8f9ff' : 'white'};
  position: relative;
  
  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  }
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const PostTextLabel = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
`;

const PostTextInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
`;

const PostButton = styled.button`
  flex: 1;
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &.primary {
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a67d8;
      transform: translateY(-1px);
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
    }
  }
  
  &.secondary {
    background: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
    
    &:hover {
      background: #e9ecef;
    }
  }
`;

const SelectSocialPlatfrom = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [showPageModal, setPageModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showPostModal, setPostModal] = useState(false);
  const [showPlatfromModal, setPlatfromModal] = useState(true);
  const [selectedPlatfrom, setSelectedPlatfrom] = useState(null);
  const [customPostText, setCustomPostText] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [registrationLink, setRegLink] = useState('');

  const eventContext = useContext(EventContext);
  const storeCampaignData = useEventStore((state) => state.eventDetails);
  const campaignData = eventContext?.campaignData ?? storeCampaignData;
  const businessAdminId = UserSessionManager.getSession()?.businessAdminId;


  const FACEBOOK_APP_ID = 755911500418069;
  const LINKEDIN_APP_ID = '77zw28l1wukz6z';
  const REDDIT_APP_ID = 'hNkGbRqL-im_FRdPLxs-fQ';
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
  const API_ORIGIN = new URL(API_BASE_URL).origin;
  const UI_BASE_URL = process.env.REACT_APP_UI_BASE_URL;
  const TEMPLATE_IMAGE_URL = process.env.REACT_APP_TEMPLATE_IMAGE_URL || (API_BASE_URL ? `${API_BASE_URL}/TemplateImages/TempEvent.png` : 'https://localhost:7080/TemplateImages/TempEvent.png');
 useEffect(() => {
  if (!campaignData?.campaignId) return;

  const baseRegLink = `${UI_BASE_URL}/view-event/${campaignData.campaignId}`;
  const params = new URLSearchParams();
  if (businessAdminId) params.set('businessAdminId', businessAdminId);
  const RegLink = params.toString() ? `${baseRegLink}?${params.toString()}` : baseRegLink;
  setRegLink(RegLink);

}, [campaignData, businessAdminId, UI_BASE_URL]);



  // Common popup opener
  const openAuthPopup = (url, platform) => {
    setIsLoading(true);
    setSelectedPlatfrom(platform);

    const popup = window.open(
      url,
      "_blank",
      "width=600,height=700,top=100,left=100"
    );

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        setIsLoading(false);
      }
    }, 500);


    const handleMessage = async (event) => {

      if (event.origin !== API_ORIGIN) return;

      const { token } = event.data;
      if (token) {
        console.log(`${platform} token received:`, token);
        localStorage.setItem(`${platform}Token`, token);

        setIsLoading(false);
        window.removeEventListener("message", handleMessage);
        popup.close();

        // Example: call backend with the token
        if (platform === "facebook") {
          try {
            const fbToken = localStorage.getItem(`${platform}Token`);
            const resp = await FacebookService.getPagesWithToken(fbToken);

            setIsLoading(false);
            setPlatfromModal(false);
            setPages(resp?.data?.data);
            setPageModal(true);
            setSelectedPlatfrom(platform);
          } catch (err) {
            console.error(err);
            setIsLoading(false);
            alert("Failed to fetch pages");
          }
        }

        if (platform === "linkedin") {
          const lkToken = localStorage.getItem(`${platform}Token`);
          setIsLoading(false);
          setPlatfromModal(false);
          setSelectedPlatfrom(platform);
          setPostModal(true);
        }

        if (platform === "reddit") {
          const radditToken = localStorage.getItem(`${platform}Token`);
          setIsLoading(false);
          setPlatfromModal(false);
          setSelectedPlatfrom(platform);
          setPostModal(true);
        }
      }
    };

    window.addEventListener("message", handleMessage);
  };

  // 🔹 Facebook Login
  const handleFacebookLogin = () => {
    const url = SocialAuthService.getFacebookAuthUrl();
    openAuthPopup(url, "facebook");
  };

  // 🔹 LinkedIn Login
  const handleLinkedInLogin = () => {
    const url = SocialAuthService.getLinkedInAuthUrl();
    openAuthPopup(url, "linkedin");
  };

  // 🔹 LinkedIn Login
  const handleRedditLogin = () => {
    const url = SocialAuthService.getRedditAuthUrl();
    openAuthPopup(url, "reddit");
  };

  // Call backend to post
  const handlePost = async () => {
    if (selectedPlatfrom === 'facebook') {
      handleFacebookPost();
    }

    if (selectedPlatfrom === 'linkedin') {
      handleLinkedinPost();
    }

    if (selectedPlatfrom === 'reddit') {
      handleRedditPost();
    }
  };

  const handleFacebookPost = async () => {
    try {
      const imageUrl = TEMPLATE_IMAGE_URL;
      const payload = {
        pageId: selectedPage.id,
        pageAccessToken: selectedPage.access_token,
        message: customPostText + " Register now - " + registrationLink,
        imageUrl,
      };

      const resp = await FacebookService.postToPage(payload);

      setPageModal(false);
      setPostModal(false);
      setIsPosting(false);
      setPlatfromModal(true);
      alert("Post successful");
    } catch (err) {
      console.error(err);
      alert("Error posting to Facebook page");
    }
  };

  const handleLinkedinPost = async () => {
    try {
      const token = localStorage.getItem("linkedinToken"); // stored during login
      if (!token) {
        alert("Please connect LinkedIn first");
        return;
      }

      const imageUrl = TEMPLATE_IMAGE_URL;
      const resp = await LinkedInService.post({
        message: customPostText + " Register now - " + registrationLink,
        imageUrl,
        accessToken: token,
      });

      console.log("LinkedIn post result:", resp);

      setPageModal(false);
      setPostModal(false);
      setIsPosting(false);
      setPlatfromModal(true);
      alert("Post successful to LinkedIn!");
    } catch (err) {
      console.error(err);
      alert("Error posting to LinkedIn");
    }
  };

  const handleRedditPost = async () => {
    try {
      const token = localStorage.getItem("redditToken"); // stored during login
      if (!token) {
        alert("Please connect Reddit first");
        return;
      }

      const imageUrl = TEMPLATE_IMAGE_URL;
      const resp = await RedditService.post({
        title: customPostText,
        subreddit: registrationLink,
        imageUrl,
        accessToken: token,
      });
      console.log("Reddit post result:", resp);

      setPageModal(false);
      setPostModal(false);
      setIsPosting(false);
      setPlatfromModal(true);
      alert("Post successful to Reddit!");
    } catch (err) {
      console.error(err);
      alert("Error posting to Reddit");
    }
  };

  const handleContinue = () => {
    if (selectedPage !== null) {
      setPageModal(false);
      setPostModal(true);
    }
  };

  return (
    <>
      {showPlatfromModal && (
        <AuthContainer>
          <h2 style={{ textAlign: 'center', marginBottom: '24px', color: '#333' }}>
            Connect Your Social Media Accounts
          </h2>

          {/* Facebook Authentication */}
          <AuthCard platform="facebook">
            <AuthHeader>
              <PlatformIcon platform="facebook">
                <FaFacebook />
              </PlatformIcon>
              <PlatformInfo>
                <PlatformName>Facebook</PlatformName>
                <PlatformDescription>
                  Connect your Facebook account to post to your profile, pages, or groups
                </PlatformDescription>
              </PlatformInfo>
            </AuthHeader>
            <AuthButton className="facebook"
              onClick={handleFacebookLogin}
              disabled={isLoading}>
              <FaFacebook />  Connect Facebook Account
            </AuthButton>
          </AuthCard>

          {/* LinkedIn Authentication */}
          <AuthCard platform="linkedin">
            <AuthHeader>
              <PlatformIcon platform="linkedin">
                <FaLinkedinIn />
              </PlatformIcon>
              <PlatformInfo>
                <PlatformName>LinkedIn</PlatformName>
                <PlatformDescription>
                  Connect your LinkedIn account to share updates on your profile or company pages
                </PlatformDescription>
              </PlatformInfo>
            </AuthHeader>
            <AuthButton className="linkedin"
              onClick={handleLinkedInLogin}
              disabled={isLoading}>
              <FaLinkedinIn /> Connect LinkedIn Account
            </AuthButton>
          </AuthCard>

          {/* Reddit Authentication */}
          <AuthCard platform="reddit">
            <AuthHeader>
              <PlatformIcon platform="reddit">
                <FaReddit />
              </PlatformIcon>
              <PlatformInfo>
                <PlatformName>Reddit</PlatformName>
                <PlatformDescription>
                  Connect your Reddit account to post to your profile or subreddits
                </PlatformDescription>
              </PlatformInfo>
            </AuthHeader>
            <AuthButton className="reddit" onClick={handleRedditLogin}
              disabled={isLoading}>
              <FaReddit /> Connect Reddit Account
            </AuthButton>
          </AuthCard>
        </AuthContainer>
      )}

      {showPageModal && (
        <>
          <FacebookPageContainer>
            <Title>Select Page for Post To</Title>
            {pages && pages.length > 0
              ? (
                <>
                  <PageGrid>
                    {pages.map((page) => (
                      <PageCard
                        key={page.id}
                        onClick={() => setSelectedPage(page)}>
                        <CheckPageIcon CheckPageIcon selected={selectedPage?.id === page.id}>
                          <FaCheck />
                        </CheckPageIcon>
                        <PageName>{page.name}</PageName>
                      </PageCard>
                    ))}
                  </PageGrid>

                  <ContinueButton
                    onClick={() => handleContinue(selectedPage)}
                    disabled={!selectedPage}   // disable until one is picked
                  >
                    Continue to Content Creation
                    {selectedPage ? " (1 selected)" : ""}
                  </ContinueButton>
                </>
              )
              : (
                <p className="text-center text-gray-500">No pages found.</p>
              )}

          </FacebookPageContainer>
        </>
      )}

      {showPostModal && (
        <AuthContainer>
          <Title>Preview & Post Your Content</Title>
          <PostTextLabel>
            Registration Link : {registrationLink}
          </PostTextLabel>
          <PostTextLabel>
            Add Post Text (Optional):
          </PostTextLabel>
          <PostTextInput
            value={customPostText}
            onChange={(e) => setCustomPostText(e.target.value)}
            placeholder="Add a caption or description to your post..."
            maxLength={500}
          />

          <PostButton
            className="primary"
            onClick={handlePost}
            disabled={isPosting}
          >
            <FaShare />
            {isPosting ? 'Posting...' : `Post to`}
          </PostButton>
        </AuthContainer>
      )}
    </>
  );
};

export default SelectSocialPlatfrom; 
